/**
 * The widget stylesheet, as a template function.
 *
 * Injected into a Shadow DOM, so none of it can reach the host page and none
 * of the host's CSS can reach it. `all: initial` on the root is the belt to
 * that braces: a host with an aggressive global reset cannot bleed through the
 * boundary via inherited properties.
 *
 * Every colour comes from a custom property so the resolved theme can be
 * swapped at runtime without rebuilding the sheet.
 */

export function styles(): string {
    return `
:host {
    all: initial;
    font-family: var(--uc-font);
}

*, *::before, *::after { box-sizing: border-box; }

.root {
    position: fixed;
    bottom: 20px;
    z-index: var(--uc-z);
    font-family: var(--uc-font);
    font-size: 14px;
    line-height: 1.4;
    color: var(--uc-text);
    direction: ltr;
}
.root[dir="rtl"] { direction: rtl; }
.root[data-position="bottom-right"] { right: 20px; }
.root[data-position="bottom-left"] { left: 20px; }

/* ---------- Launcher ---------- */

.fab {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border: none;
    border-radius: 50%;
    background: var(--uc-primary);
    color: var(--uc-on-primary);
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    transition: transform 120ms ease, box-shadow 120ms ease;
}
.fab:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22); }
.fab:focus-visible { outline: 3px solid var(--uc-primary); outline-offset: 3px; }
.fab svg { width: 26px; height: 26px; }
.fab[hidden] { display: none; }

/* ---------- Panel ---------- */

.panel {
    display: flex;
    flex-direction: column;
    width: 400px;
    height: min(640px, calc(100vh - 40px));
    max-height: calc(100vh - 48px);
    min-height: 0;
    background: var(--uc-surface);
    border: 1px solid var(--uc-border);
    border-radius: var(--uc-radius);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    overflow: hidden;
}
.panel[hidden] { display: none; }

.header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    padding-top: max(14px, env(safe-area-inset-top, 0px));
    background: var(--uc-primary);
    color: var(--uc-on-primary);
    flex: 0 0 auto;
    min-height: 56px;
    position: relative;
    z-index: 2;
}
.header-logo {
    width: 28px; height: 28px;
    border-radius: 6px;
    object-fit: contain;
    background: rgba(255, 255, 255, 0.18);
    flex-shrink: 0;
}
.header-logo[hidden] { display: none; }
.header-text { flex: 1; min-width: 0; }
.header-title {
    font-weight: 600;
    font-size: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.header-subtitle {
    font-size: 11px;
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
}
.header-subtitle[hidden] { display: none; }
.header-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: 0.9;
    margin-top: 2px;
}
.status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #86efac;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px rgba(134, 239, 172, 0.35);
}
.header-status[data-state="offline"] .status-dot {
    background: #fca5a5;
    box-shadow: 0 0 0 2px rgba(252, 165, 165, 0.35);
}
.header-status[data-state="connecting"] .status-dot {
    background: #fde68a;
    box-shadow: 0 0 0 2px rgba(253, 230, 138, 0.35);
}

.icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px; height: 32px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: inherit;
    cursor: pointer;
    flex-shrink: 0;
}
.icon-button:hover { background: rgba(255, 255, 255, 0.18); }
.icon-button:focus-visible { outline: 2px solid var(--uc-on-primary); outline-offset: 1px; }
.icon-button svg { width: 18px; height: 18px; }

/* ---------- Messages ---------- */

.messages {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 14px 16px;
    background: var(--uc-panel);
    display: flex;
    flex-direction: column;
    gap: 14px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
}

.msg { display: flex; flex-direction: column; max-width: 92%; }
.msg[data-role="user"] { align-self: flex-end; align-items: flex-end; }
.msg[data-role="assistant"] { align-self: flex-start; align-items: flex-start; }

.bubble {
    padding: 10px 13px;
    border-radius: var(--uc-radius);
    overflow-wrap: anywhere;
}
.msg[data-role="user"] .bubble {
    white-space: pre-wrap;
    background: var(--uc-user-bubble);
    color: var(--uc-user-bubble-text);
    border-bottom-right-radius: 4px;
}
.msg[data-role="assistant"] .bubble {
    background: var(--uc-bot-bubble);
    color: var(--uc-text);
    border: 1px solid var(--uc-border);
    border-bottom-left-radius: 4px;
}
.bubble-p {
    margin: 0 0 8px;
    line-height: 1.45;
}
.bubble-p:last-child { margin-bottom: 0; }
.bubble-list {
    margin: 0;
    padding-inline-start: 1.15em;
    display: flex;
    flex-direction: column;
    gap: 7px;
}
.bubble-list li {
    margin: 0;
    padding: 0;
    line-height: 1.45;
}
:host([dir="rtl"]) .msg[data-role="user"] .bubble {
    border-bottom-right-radius: var(--uc-radius);
    border-bottom-left-radius: 4px;
}
:host([dir="rtl"]) .msg[data-role="assistant"] .bubble {
    border-bottom-left-radius: var(--uc-radius);
    border-bottom-right-radius: 4px;
}

.timestamp { font-size: 11px; color: var(--uc-muted); margin-top: 4px; }

.empty-state {
    color: var(--uc-muted);
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
    padding: 28px 16px;
}

/* ---------- Navigation card ---------- */

.nav-card {
    margin-top: 8px;
    padding: 12px;
    border: 1px solid var(--uc-border);
    border-radius: var(--uc-radius);
    background: var(--uc-surface);
    width: 100%;
}
.nav-card-title { font-size: 13px; color: var(--uc-muted); margin-bottom: 8px; }
.nav-link {
    color: var(--uc-primary);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
}
.nav-link:hover { filter: brightness(0.9); }
.nav-link:focus-visible { outline: 3px solid var(--uc-primary); outline-offset: 2px; }
.bubble-link {
    color: var(--uc-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    overflow-wrap: anywhere;
}
.bubble-link:hover { filter: brightness(0.9); }

/* ---------- Sources ---------- */

.sources { margin-top: 8px; width: 100%; }
.sources-summary {
    font-size: 12px;
    color: var(--uc-muted);
    cursor: pointer;
    padding: 2px 0;
}
.sources-summary:focus-visible { outline: 2px solid var(--uc-primary); outline-offset: 2px; }
.source-item {
    font-size: 12px;
    color: var(--uc-muted);
    padding: 4px 0 4px 10px;
    border-inline-start: 2px solid var(--uc-border);
    margin-top: 4px;
}
.source-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--uc-primary);
    font-family: inherit;
    font-size: 12px;
    text-align: start;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.source-link:hover { filter: brightness(0.9); }
.source-link:focus-visible { outline: 2px solid var(--uc-primary); outline-offset: 2px; }
.source-link svg { width: 12px; height: 12px; flex-shrink: 0; }

/* ---------- Typing indicator ---------- */

.typing { display: flex; gap: 4px; padding: 12px 13px; }
.typing span {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: var(--uc-muted);
    animation: uc-bounce 1.2s infinite ease-in-out;
}
.typing span:nth-child(2) { animation-delay: 0.15s; }
.typing span:nth-child(3) { animation-delay: 0.3s; }

@keyframes uc-bounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
    30% { transform: translateY(-4px); opacity: 1; }
}

/* ---------- Suggestions ---------- */

.suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 2px 0 6px;
    background: transparent;
    flex: 0 0 auto;
    min-height: 0;
    max-width: 100%;
}
.suggestions[hidden] { display: none; }
.chip {
    padding: 10px 14px;
    min-height: 40px;
    border: 1px solid var(--uc-border);
    border-radius: 999px;
    background: var(--uc-surface);
    color: var(--uc-text);
    font-family: inherit;
    font-size: 13px;
    line-height: 1.35;
    cursor: pointer;
    text-align: start;
    touch-action: manipulation;
}
.chip:hover { background: var(--uc-bot-bubble); border-color: var(--uc-primary); }
.chip:focus-visible { outline: 2px solid var(--uc-primary); outline-offset: 2px; }

/* ---------- Composer ---------- */

.composer {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px 14px;
    padding-bottom: max(12px, env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--uc-border);
    background: var(--uc-surface);
    flex: 0 0 auto;
}
.input {
    flex: 1;
    min-height: 44px;
    max-height: 120px;
    padding: 11px 13px;
    border: 1px solid var(--uc-border);
    border-radius: 10px;
    background: var(--uc-surface);
    color: var(--uc-text);
    font-family: inherit;
    font-size: 15px;
    line-height: 1.4;
    resize: none;
    overflow-y: auto;
}
.input:focus { outline: 2px solid var(--uc-primary); outline-offset: -1px; border-color: var(--uc-primary); }
.input::placeholder { color: var(--uc-muted); }

.send {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px; height: 44px;
    border: none;
    border-radius: 10px;
    background: var(--uc-primary);
    color: var(--uc-on-primary);
    cursor: pointer;
    flex-shrink: 0;
}
.send:disabled { opacity: 0.45; cursor: not-allowed; }
.send:focus-visible { outline: 3px solid var(--uc-primary); outline-offset: 2px; }
.send svg { width: 18px; height: 18px; }
/* English: arrow points left-to-right. Arabic: the opposite direction. */
.root[dir="rtl"] .send svg { transform: scaleX(-1); }

.error-banner {
    padding: 9px 14px;
    background: #fef2f2;
    color: #991b1b;
    font-size: 13px;
    border-top: 1px solid #fecaca;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
}
.error-banner[hidden] { display: none; }
.error-retry {
    border: none;
    background: transparent;
    color: inherit;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
    flex-shrink: 0;
}

/* Screen-reader-only live region. */
.sr-only {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
}

/* ---------- Mobile ---------- */

@media (max-width: 480px) {
    .root { bottom: 12px; }
    .root[data-position="bottom-right"] { right: 12px; }
    .root[data-position="bottom-left"] { left: 12px; }

    .root.is-compact {
        inset: 0;
        right: auto;
        bottom: auto;
        width: 100%;
        height: 100%;
    }
    .root.is-compact .panel {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        max-height: none;
        border-radius: 0;
        border: none;
    }
    .input { font-size: 16px; }
    .icon-button, .send, .fab { touch-action: manipulation; }
}

/* Keyboard open: keep the title, tighten chrome, keep chips reachable. */
.root.is-keyboard .header {
    padding: 10px 12px;
    padding-top: max(10px, env(safe-area-inset-top, 0px));
    min-height: 52px;
}
.root.is-keyboard .header-title { display: block; }
.root.is-keyboard .header-subtitle,
.root.is-keyboard .header-status { display: none; }
.root.is-keyboard .suggestions {
    max-height: 96px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
}
.root.is-keyboard .messages { scroll-behavior: auto; }
.root.is-keyboard .composer {
    padding: 8px 12px;
    padding-bottom: max(8px, env(safe-area-inset-bottom, 0px));
}

/* Honour a user who has asked the platform for less motion. */
@media (prefers-reduced-motion: reduce) {
    .fab, .messages { transition: none; scroll-behavior: auto; }
    .typing span { animation: none; opacity: 0.6; }
}
`;
}
